import { FC, FocusEvent, FormEvent, useEffect, useRef, useState } from 'react'
import SearchSvg from '@/public/images/common/search-lg_v2.svg'
import classNames from 'classnames'
import { TextTag2 } from '@components-v2/atoms/TextTag2'
import styles from './index.module.scss'

type Props = {
  placeholder?: string
  name: string
  value: string | number | readonly string[]
  testid?: string
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void
  onChange?: (e: FormEvent<HTMLInputElement>) => void
  focused?: boolean
  selectedItems?: { id: string; name: string }[]
  onDeleteSelectedItem?: (id: string) => void
  onDeletedAllSelectedItems?: () => void
  fullWidth?: boolean
}

// 選択済みのタグの最大数
const MAX_TAG_COUNT = 1

export const SearchInputField: FC<Props> = ({
  placeholder = '',
  name = '',
  value,
  testid = '',
  onChange = () => {},
  onBlur = () => {},
  onFocus = () => {},
  focused = false,
  selectedItems = [],
  onDeleteSelectedItem = () => {},
  onDeletedAllSelectedItems = () => {},
  fullWidth = false,
}) => {
  const [leftSpaceWidth, setLeftSpaceWidth] = useState(0)
  const [isLeftSpaceHovered, setIsLeftSpaceHovered] = useState(false)

  const leftRef = useRef<HTMLDivElement>()
  const inputRef = useRef<HTMLInputElement>()

  const handleLeftSpaceClick = () => {
    inputRef.current?.focus()
  }

  const handleLeftSpaceMouseEnter = () => {
    setIsLeftSpaceHovered(true)
  }

  const handleLeftSpaceMouseLeave = () => {
    setIsLeftSpaceHovered(false)
  }

  // ResizeObserver を使わないシンプルな方法があればそちらに変更する
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.borderBoxSize[0]?.inlineSize
        if (!width) {
          return
        }

        setLeftSpaceWidth(width)
      })

      resizeObserver.observe(leftRef.current)

      return () => {
        resizeObserver.disconnect()
      }
    }

    return () => {}
  }, [])

  const shouldShowTags = selectedItems?.length > 0

  // TODO あとで調整
  let inputSize = 0
  if (placeholder.length < 5) {
    inputSize = 7
  } else if (placeholder.length < 8) {
    inputSize = 15
  } else if (placeholder.length < 10) {
    inputSize = 17
  } else {
    inputSize = 20
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullWidth]: fullWidth,
      })}
    >
      <div
        role='button'
        className={styles.leftSpace}
        ref={leftRef}
        onClick={handleLeftSpaceClick}
        onKeyPress={handleLeftSpaceClick}
        onMouseEnter={handleLeftSpaceMouseEnter}
        onMouseLeave={handleLeftSpaceMouseLeave}
        tabIndex={0}
      >
        <div className={styles.leftIcon}>
          <SearchSvg viewBox='0 0 24 24' />
        </div>
        {shouldShowTags &&
          (selectedItems.length <= MAX_TAG_COUNT ? (
            <div className={styles.tags}>
              {selectedItems.map((item) => (
                <TextTag2 size='md' withDelete onDelete={() => onDeleteSelectedItem(item.id)} key={item.id}>
                  {item.name}
                </TextTag2>
              ))}
            </div>
          ) : (
            <div className={styles.tags}>
              <TextTag2
                size='md'
                withDelete
                onDelete={onDeletedAllSelectedItems}
              >{`${placeholder} (${selectedItems.length})`}</TextTag2>
            </div>
          ))}
      </div>
      <input
        ref={inputRef}
        className={classNames(
          styles.input,
          { [styles.focused]: focused },
          { [styles.hovered]: inputRef.current ? isLeftSpaceHovered : false },
          {
            [styles.withTags]: shouldShowTags,
          }
        )}
        size={inputSize}
        type='text'
        placeholder={placeholder}
        name={name}
        data-testid={testid}
        value={value}
        onChange={onChange}
        onBlur={(e) => {
          onBlur(e)
          setIsLeftSpaceHovered(false)
        }}
        onFocus={(e) => {
          onFocus(e)
        }}
        style={{ paddingLeft: leftSpaceWidth + 14 }}
      />
    </div>
  )
}
